import { useRef } from 'react'

import {
  AtsGetSalaryConditionsDocument,
  AtsJobPostingSalaryFormatEnum,
  AtsJobPostingSalaryPeriodEnum,
} from 'generated/resources/graphql'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'

interface SalaryFormValues {
  salaryFormat: AtsJobPostingSalaryFormatEnum
  salaryPeriod: AtsJobPostingSalaryPeriodEnum | null
  salaryFromAmountInCents: number | null
  salaryToAmountInCents: number | null
}

interface UseSalaryConditionsAutoFillParams {
  jobCatalogLevelUuid: string | null
  workplaceId: string
  onSalaryConditionsLoaded: (values: SalaryFormValues) => void
}

const isValidPeriodicity = (value: string): value is AtsJobPostingSalaryPeriodEnum =>
  value === 'annual' || value === 'monthly' || value === 'daily'

export const useSalaryConditionsAutoFill = ({
  jobCatalogLevelUuid,
  workplaceId,
  onSalaryConditionsLoaded,
}: UseSalaryConditionsAutoFillParams) => {
  const salaryValues = useRef(onSalaryConditionsLoaded)
  salaryValues.current = onSalaryConditionsLoaded

  const { data: salaryConditionsData } = useQuery(AtsGetSalaryConditionsDocument, {
    variables: {
      levelNodeUuid: jobCatalogLevelUuid ?? '',
      workplaceId: workplaceId,
    },
    skip: !jobCatalogLevelUuid || !workplaceId,
  })

  const salaryConditions = salaryConditionsData?.ats.salaryConditionsConnection.nodes[0]

  useUnsafeEffect(() => {
    if (!salaryConditions) {
      return
    }

    const { min, max, gross, periodicity } = salaryConditions

    const salaryFormat: AtsJobPostingSalaryFormatEnum = gross ? 'fixed_amount' : 'range'
    const salaryFrom = gross ?? min
    const salaryTo = max
    const salaryPeriod: AtsJobPostingSalaryPeriodEnum | null = isValidPeriodicity(periodicity)
      ? periodicity
      : null

    salaryValues.current({
      salaryFormat,
      salaryPeriod,
      salaryFromAmountInCents: salaryFrom ?? null,
      salaryToAmountInCents: salaryTo ?? null,
    })
  }, [salaryConditions])
}
